/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import { useOrganizationModalContext } from '../../context'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

const Settings = () => {
  const { state, setState } = useOrganizationModalContext()

  return (
    <div
      css={css`
        display: flex;
        padding: 16px;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Settings
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Request Waiver'
          subtitle={
            'Players Will Receive And Be Required To Sign The Waiver(S) With Their Invite, In Order To Accept And Join Each Team.'
          }
          value={state.show_waiver}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              show_waiver: value,
              waiver_id: value ? prevState.waiver_id : '',
              waivers: value ? prevState.waivers : [],
            }))
          }}
        />
      </div>
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
        style={'align-self: flex-end;'}
      >
        To set up or manage waivers, please go to the waiver(s) section in your{' '}
        <Link
          to={`${ROUTES.GENERAL_SETTINGS}?origin=waiver`}
          target='_blank'
          css={css`
            color: ${colors.PRIMARY};
          `}
        >
          Account Settings.
        </Link>
      </Text>
      {state?.show_waiver ? (
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <SelectInput
              label='Waiver'
              options={state.waivers}
              value={state.waiver_id}
              onChange={(value) => {
                setState({
                  ...state,
                  waiver_id: value,
                })
              }}
              fieldErrors={state.fieldErrors}
              customError={state.customError}
              disabled={false}
              name='waiver_id'
            />
          </div>
        </div>
      ) : null}
      {/* Separator */}
      <div
        css={css`
          width: 100%;
          height: 1px;
          background-color: ${colors.SOFT_STEEL};
        `}
      />
      {/* Leaderboards Settings */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Leaderboards Settings
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Display Leaderboards'
          subtitle='Enable this option to display the leaderboards section on the organization.'
          value={state.display_leaderboards}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              display_leaderboards: value,
            }))
          }}
        />
      </div>
      {/* Separator */}
      <div
        css={css`
          width: 100%;
          height: 1px;
          background-color: ${colors.SOFT_STEEL};
        `}
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title={isCanlan ? 'Can Contain Leagues' : 'Can Contain Organizations'}
          subtitle='Allow sub-organizations to be created within this organization.'
          value={state.can_contain_organizations}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_organizations: value,
            }))
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Can Contain Teams'
          subtitle='Allow teams to be created within this organization.'
          value={state.can_contain_teams}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_teams: value,
            }))
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title={isCanlan ? 'Can Contain Seasons' : 'Can Contain Competitions'}
          subtitle='Allow competitions/schedules to be created within this organization.'
          value={state.can_contain_schedules}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_schedules: value,
            }))
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title={
            isCanlan ? 'Can Contain Sports Complexes' : 'Can Contain Locations'
          }
          subtitle='Allow venues/locations to be created within this organization.'
          value={state.can_contain_locations}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_locations: value,
            }))
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Can Contain Feed'
          subtitle='Allow social feed posts within this organization.'
          value={state.can_contain_feeds}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_feeds: value,
            }))
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
          display: none;
        `}
      >
        <SwitchInput
          title='Display Schedule'
          subtitle='Display the schedule (games) tab for this organization.'
          value={state.can_contain_games}
          onChange={(value) => {
            setState((prevState) => ({
              ...prevState,
              can_contain_games: value,
            }))
          }}
        />
      </div>
    </div>
  )
}

export default Settings
